import { Tree, useTree, RenderTreeNodePayload, TreeNodeData } from "@mantine/core";
import { useMemo } from "react";

import { PartWithChildrenDto } from "#modules/parts/infra/fetchPartWithChildren.api";
import { Icon } from "#shared/icons/Icon";
import { Box } from "#shared/view/components/Box";
import { Group } from "#shared/view/components/Group";
import { Paper } from "#shared/view/components/Paper";
import { Text } from "#shared/view/components/Text";
import { ThumbnailOverlay } from "#shared/view/components/Thumbnails/ThumbnailOverlay";

const ROW_HEIGHT = 40;
const PADDING = 2;

const toTreeData = (part: PartWithChildrenDto): TreeNodeData => {
  const children = part.children as unknown as PartWithChildrenDto[];

  return {
    value: part.id,
    label: part.name,
    ...(children.length > 0 ? { children: children.map((child) => toTreeData(child)) } : {}),
  };
};

const buildPartMap = (part: PartWithChildrenDto, map: Map<string, PartWithChildrenDto>): void => {
  map.set(part.id, part);
  (part.children as unknown as PartWithChildrenDto[]).forEach((child) => buildPartMap(child, map));
};

export const PartTree = ({
  part,
  onSelect,
}: {
  part: PartWithChildrenDto;
  onSelect: (part: PartWithChildrenDto) => void;
}) => {
  const partMap = useMemo(() => {
    const map = new Map<string, PartWithChildrenDto>();
    buildPartMap(part, map);
    return map;
  }, [part]);

  const treeData = useMemo(() => {
    return [toTreeData(part)];
  }, [part]);

  const tree = useTree({
    initialExpandedState: { [part.id]: true },
  });

  return (
    <Tree
      data={treeData}
      tree={tree}
      selectOnClick
      renderNode={(payload) => (
        <NodeRenderer payload={payload} partMap={partMap} onSelect={onSelect} />
      )}
    />
  );
};

type NodeRendererProps = {
  payload: RenderTreeNodePayload;
  partMap: Map<string, PartWithChildrenDto>;
  onSelect: (part: PartWithChildrenDto) => void;
};

const INDENT = 16;

const NodeRenderer = ({ payload, partMap, onSelect }: NodeRendererProps) => {
  const { node, tree, elementProps, hasChildren, expanded, level } = payload;

  const partData = partMap.get(node.value) ?? null;

  if (!partData) {
    return null;
  }

  const isLeaf = !hasChildren;

  return (
    <Paper
      {...elementProps}
      style={{
        ...elementProps.style,
        cursor: hasChildren ? "pointer" : "default",
      }}
      bg={elementProps["data-selected"] ? "blue.2" : undefined}
      py={PADDING}
      pl={level * INDENT}
      onClick={(e) => {
        elementProps.onClick(e);
        onSelect(partData);
      }}
    >
      <Group wrap="nowrap" gap={"xxs"} justify="flex-start">
        <Box
          ml={isLeaf ? 17 : 0}
          onClick={(e: React.MouseEvent) => {
            if (hasChildren) {
              e.stopPropagation();
              tree.toggleExpanded(node.value);
            }
          }}
        >
          {isLeaf ? (
            ""
          ) : !expanded ? (
            <Icon name="plus" size={17} />
          ) : (
            <Icon name="minus" size={17} />
          )}
        </Box>

        {partData.urlPng3D ? (
          <ThumbnailOverlay
            part={partData}
            size={{
              width: ROW_HEIGHT - 2 * PADDING,
              height: ROW_HEIGHT - 2 * PADDING,
            }}
          />
        ) : (
          <Icon name="cube" />
        )}
        <Text inline lineClamp={1}>
          {partData.name.replace(/[\s_]/g, " ")}
        </Text>
      </Group>
    </Paper>
  );
};
